// Wearable firmware for the update screens, from two places: the API
// (/v2/wearable-firmware/, superadmins) and the copies kept on this computer
// (electron/firmwareReleases.js) for updating with no internet. Both are
// normalized to one shape:
//
//   { tag, version, channel, productVersion, baseVersion, releasedAt, size,
//     sha256, saved }
//
// `tag` names the release everywhere (v1.2.0, v0.9.0-dev.a1b2c3d); `saved`
// means its zip is on this computer.

export const FIRMWARE_CHANNELS = ["production", "dev"];

/** An API release, list row or latest/version response. */
export const fromApi = (r) => ({
  tag: r.tag,
  version: r.version || r.tag,
  channel: r.channel || null,
  productVersion: r.product_version || null,
  baseVersion: r.base_version || null,
  releasedAt: r.released_at || null,
  size: r.size ?? r.artifacts?.ota?.size ?? null,
  sha256: r.artifacts?.ota?.sha256 || null,
  saved: false,
});

/** A row from the computer's firmware index. */
export const fromLocal = (r) => ({
  tag: r.tag,
  version: r.version || r.tag,
  channel: r.channel || null,
  productVersion: r.productVersion || null,
  baseVersion: r.baseVersion || null,
  releasedAt: r.releasedAt || null,
  size: r.size ?? null,
  sha256: r.sha256 || null,
  saved: true,
});

const newestFirst = (a, b) =>
  String(b.releasedAt || "").localeCompare(String(a.releasedAt || ""));

/**
 * A range-test build (status every 1 s, for BLE range walks). Published with
 * the dev builds but never as the latest, so never picked as a default here.
 */
export const isRangeTest = (tag) => /-rangetest(?:-|$)/.test(tag || "");

/** The newest copy of a channel's firmware on this computer, or null. */
export const newestSaved = (localRows, channel) =>
  (localRows || [])
    .map(fromLocal)
    .filter((r) => r.channel === channel && !isRangeTest(r.tag))
    .sort(newestFirst)[0] || null;

/**
 * A channel's releases for the "previous versions" list: the API's (when
 * reachable) plus any saved copies it no longer lists, newest first, with
 * `saved` set from what's on this computer.
 */
export const mergeReleases = (apiRows, localRows, channel) => {
  const saved = new Map(
    (localRows || [])
      .filter((r) => r.channel === channel)
      .map((r) => [r.tag, r])
  );
  const byTag = new Map();
  for (const row of apiRows || []) {
    if (!row?.tag || byTag.has(row.tag)) continue;
    byTag.set(row.tag, { ...fromApi(row), saved: saved.has(row.tag) });
  }
  for (const [tag, row] of saved) {
    if (!byTag.has(tag)) byTag.set(tag, fromLocal(row));
  }
  return [...byTag.values()].sort(newestFirst);
};

/** True when a request failed for want of a network, not with an HTTP error. */
export const isOfflineError = (e) => !!e && e.status == null;

/** The version to show: the product version for production, else the build. */
export const releaseLabel = (r) =>
  (r?.channel === "production" && r.productVersion) || r?.version || r?.tag;

export const releaseZipName = (tag) => `SIT_FW_${tag}_OTA.zip`;
